import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody, CardFooter, CardHeader, Col, Row } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import getChartColorsArray from '../../../../../Components/Common/ChartsDynamicColor';
import { useDispatch, useSelector } from 'react-redux';
import { getDataReportSalinity } from '../../../../../slices/thunks';
import config from '../../../../../config';

const SalinitySummary = ({ title, um, code_siep, h100 = true, height = 150, showHeader = true, showFooter = true }) => {
    const dispatch = useDispatch();
    const [dataInterval, setDataInterval] = useState('24h');

    const allowedDataIntervals = ['24h', '7d', '15d', '1m'];

    const dataColors = '["--vz-primary", "--vz-secondary"]';
    const revenueExpensesChartsColors = getChartColorsArray(dataColors);

    const { data, isLoading, isSuccess, isError } = useSelector((state) => state.SalinityProvider || {});

    useEffect(() => {
        const requestData = {
            username: config.user_providers.USER,
            password: config.user_providers.PASS,
            service: 'innovex',
            interval: dataInterval,
            siep: code_siep,
            variable: 'salinity',
        };

        if (code_siep && code_siep !== 'all') {
            dispatch(getDataReportSalinity(requestData));
        }
    }, [dataInterval, code_siep, dispatch]);

    const seriesData = useMemo(() => {
        if (!data || !data.time || !data['5m'] || !data['10m']) {
            return [
                { name: '5m', data: [] },
                { name: '10m', data: [] },
            ];
        }

        const timeValues = Object.values(data.time);
        const data5m = Object.values(data['5m']);
        const data10m = Object.values(data['10m']);

        return [
            {
                name: '5m',
                data: timeValues.map((t, i) => ({
                    x: new Date(t).getTime(),
                    y: data5m[i],
                })),
            },
            {
                name: '10m',
                data: timeValues.map((t, i) => ({
                    x: new Date(t).getTime(),
                    y: data10m[i],
                })),
            },
        ];
    }, [data]);


    const calculateStats = data => {
        if (!Array.isArray(data)) return { min: '--', max: '--', avg: '--' };
        const values = data.map(item =>
            Array.isArray(item) ? item[1] :
            (typeof item === 'object' && item !== null ? item.y : item)
        );
        const validData = values.filter(val => Number.isFinite(val));
        if (validData.length === 0) return { min: '--', max: '--', avg: '--' };

        const sum = validData.reduce((acc, val) => acc + val, 0);
        return {
            min: Math.min(...validData),
            max: Math.max(...validData),
            avg: (sum / validData.length).toFixed(1),
        };
    };

    const stats = useMemo(() => {
        return seriesData.map(serie => ({
            name: serie.name,
            stats: calculateStats(serie.data),
        }));
    }, [seriesData]);

    const Charts = ({ dataColors, series }) => {
        const safeSeries = JSON.parse(JSON.stringify(series || []));

        const options = {
            chart: {
                height: 200,
                type: 'area',
                toolbar: false,
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                curve: 'smooth',
                width: 1,
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    rotate: -70,
                    style: { fontSize: '8px' },
                    datetimeUTC: false,
                },
                tickAmount: 6,
            },
            yaxis: {
                show: true,
                labels: {
                    formatter: function (value) {
                        return `${value.toFixed(0)} ` + um;
                    },
                    style: {
                        fontSize: '8px',
                    },
                },
                min: 0
            },
            colors: revenueExpensesChartsColors,
            fill: {
                opacity: 0.06,
                colors: revenueExpensesChartsColors,
                type: 'solid',
            },
            tooltip: {
                y: {
                    formatter: function (value) {
                        return `${value} ` + um;
                    },
                },
            },
            legend: {
                show: false, // Desactiva la leyenda
            },
        };

        return (
            <ReactApexChart
                options={{
                    ...options,
                    noData: {
                        text: "No hay datos disponibles",
                        align: "center",
                        verticalAlign: "middle",
                        style: {
                            color: "#999",
                            fontSize: "11px",
                        },
                    },
                }}
                series={safeSeries}
                type="area"
                height={height}
                className="apex-charts"
            />
        );
    };

    return (
        <Card style={h100 ? { height: '100%' } : {}}>
            {showHeader && (
                <CardHeader className="p-2 align-items-center d-flex">
                    <i className="ri-pulse-line align-middle me-1 text-muted" style={{ fontSize: 16 }}></i>
                    <h5 className="card-title mb-0 flex-grow-1">{title}</h5>
                    <div className="btn-box-dashboard">
                        {allowedDataIntervals.map(type => (
                            <Button
                                key={type}
                                color={dataInterval === type ? 'secondary' : 'primary'}
                                size="sm"
                                className={`btn-soft-secondary ${dataInterval === type ? 'active' : ''}`}
                                onClick={() => setDataInterval(type)}
                            >
                                {type.toUpperCase()}
                            </Button>
                        ))}
                    </div>
                </CardHeader>
            )}
            <CardBody className="p-0 m-0">
                <div id="revenue-expenses-charts" dir="ltr" style={{ paddingLeft: '0px', paddingRight: '5px' }}>
                    <Charts series={seriesData} />
                </div>
            </CardBody>
            {showFooter && (
                <CardFooter className='p-0'>
                    <Row className="m-2 p-0">
                        {(data?.summary ? Object.entries(data.summary) : []).map(([key, values = {}], index) => (
                            <Col
                                key={index}
                                md={6}
                                className={`m-0 p-0 ${index !== 0 ? 'border-start' : ''}`}
                            >
                                <h5 className="m-0 text-center" style={{ color: revenueExpensesChartsColors[index] }}>
                                    {key}
                                </h5>
                                <ul className="list-inline main-chart text-center mb-0">
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">
                                            {values.min || '--'}
                                        </h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Mínimo</span>
                                    </li>
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">
                                            {values.avg || '--'}
                                        </h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Promedio</span>
                                    </li>
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">
                                            {values.max || '--'}
                                        </h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Máximo</span>
                                    </li>
                                </ul>
                            </Col>
                        ))}

                        {/* Fallback calculado desde seriesData cuando no hay summary */}
                        {(!data?.summary || Object.keys(data.summary).length === 0) && (
                            <Col md={12} className="m-0 p-0">
                                <h5 className="m-0 text-center">{stats[0]?.name ?? 'Sin datos'}</h5>
                                <ul className="list-inline main-chart text-center mb-0">
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">{stats[0]?.stats?.min !== '--' ? `${stats[0].stats.min} ${um}` : '--'}</h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Mínimo</span>
                                    </li>
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">{stats[0]?.stats?.avg !== '--' ? `${stats[0].stats.avg} ${um}` : '--'}</h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Promedio</span>
                                    </li>
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">{stats[0]?.stats?.max !== '--' ? `${stats[0].stats.max} ${um}` : '--'}</h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Máximo</span>
                                    </li>
                                </ul>
                            </Col>
                        )}
                    </Row>
                </CardFooter>
            )}
        </Card>
    );
};

export default SalinitySummary;
